/**
 * Section components for **Aggregator** entity-kind in the Entity
 * Details panel. Renders the schema from ADR-024 §4 + Flow 04
 * §"Panel content per entity type" (Aggregator row):
 *
 *   1. STATUS — pill (enabled / disabled / archived), mode,
 *      trust mode.
 *   2. CONNECTIVITY — placeholder pending connector-state
 *      telemetry (same blocker as Platform CONNECTIVITY).
 *   3. MESSAGE RATES — placeholder pending aggregate-rate query.
 *   4. MULTIPLEXED PLATFORMS — placeholder pending grouped query.
 *   5. CONFIGURATION — display_name, organisation, classification,
 *      cert binding, onboarding provenance.
 *   6. RECENT ERRORS — placeholder pending error-stream source.
 *
 * Section **visibility** and **expand/collapse** are read from
 * :func:`useSectionVisibility("aggregator")` per REQ-UI-092 / REQ-UI-064
 * — sections the user has unchecked in the gear menu are elided entirely
 * (header + body, ADR-024 §4), and each rendered shell self-wires its
 * collapsed default off the :func:`EntityDetailsSectionsProvider`.
 *
 * Cross-references:
 *   - REQ-UI-092 (per-type visibility + expand/collapse defaults).
 *   - REQ-UI-064 (collapsible sections, all entity types).
 *   - REQ-UI-049 (Aggregator section schema).
 *   - ADR-024 §4; ADR-033 §10.
 *   - Flow 04 §"Panel content per entity type" (Aggregator row).
 */
import { SectionShell, SectionRow, SectionPlaceholder } from "@/entity-details/section-shell";
import { useAggregators, type AggregatorListItem } from "@/entity-details/use-aggregators";
import { useSectionVisibility } from "@/entity-details/use-section-visibility";
import { EntityDetailsSectionsProvider } from "@/entity-details/entity-details-sections-context";
import { recordTypeForEntityKind } from "@/entity-details/entity-record-mapping";
import { SourceBytesSection } from "@/query/source-bytes-section";

export function AggregatorSections({ aggregatorId }: { aggregatorId: string }): React.ReactElement {
  const query = useAggregators();
  const record = query.data?.items.find((a) => a.aggregator_id === aggregatorId) ?? null;
  const sections = useSectionVisibility("aggregator");
  const { isVisible } = sections;
  const error = query.isError ? query.error.message : null;
  return (
    <EntityDetailsSectionsProvider value={{ entityType: "aggregator", sections }}>
      {isVisible("status") && (
        <StatusSection record={record} loading={query.isLoading} error={error} />
      )}
      {isVisible("connectivity") && <ConnectivitySection />}
      {isVisible("message_rates") && <MessageRatesSection />}
      {isVisible("multiplexed_platforms") && <MultiplexedPlatformsSection />}
      {isVisible("configuration") && (
        <ConfigurationSection record={record} loading={query.isLoading} error={error} />
      )}
      {isVisible("recent_errors") && <RecentErrorsSection />}
      {/* An aggregator is a Provider (ADR-040) but still registers like any
          producer, so its registration row is in the same table under
          ``scope = AGGREGATOR``. Included so this pane is not the one entity
          pane without the section (REQ-UI-184). */}
      {isVisible("source_bytes") && (
        <SourceBytesSection
          typeId={recordTypeForEntityKind("aggregator")}
          recordId={aggregatorId}
        />
      )}
    </EntityDetailsSectionsProvider>
  );
}

interface RecordProps {
  record: AggregatorListItem | null;
  loading: boolean;
  error: string | null;
}

function StatusSection({ record, loading, error }: RecordProps): React.ReactElement {
  return (
    <SectionShell id="status" title="Status">
      {loading && <SectionPlaceholder note="Loading aggregator…" />}
      {error !== null && <SectionPlaceholder note={`Could not load: ${error}`} />}
      {!loading && error === null && record === null && (
        <SectionPlaceholder note="Aggregator not found in the current scope." />
      )}
      {record !== null && (
        <>
          <SectionRow label="Status" value={record.status} />
          <SectionRow label="Mode" value={record.mode} />
          <SectionRow label="Trust mode" value={record.trust_mode} />
        </>
      )}
    </SectionShell>
  );
}

function ConnectivitySection(): React.ReactElement {
  return (
    <SectionShell id="connectivity" title="Connectivity">
      <SectionPlaceholder note="Connector ID, session uptime, and reconnect history pending connector-state telemetry." />
    </SectionShell>
  );
}

function MessageRatesSection(): React.ReactElement {
  return (
    <SectionShell id="message_rates" title="Message rates">
      <SectionPlaceholder note="Aggregate message rates (60s rolling) pending a per-aggregator rates endpoint." />
    </SectionShell>
  );
}

function MultiplexedPlatformsSection(): React.ReactElement {
  return (
    <SectionShell id="multiplexed_platforms" title="Multiplexed platforms">
      <SectionPlaceholder note="Producer count by status pending a grouped-producers query. Use the Platforms panel for now." />
    </SectionShell>
  );
}

function ConfigurationSection({ record, loading, error }: RecordProps): React.ReactElement {
  return (
    <SectionShell id="configuration" title="Configuration">
      {loading && <SectionPlaceholder note="Loading configuration…" />}
      {error !== null && <SectionPlaceholder note={`Could not load: ${error}`} />}
      {!loading && error === null && record !== null && (
        <>
          <SectionRow label="Display name" value={record.display_name} />
          <SectionRow label="Organization" value={record.organization} />
          <SectionRow label="Classification" value={record.classification} />
          <SectionRow label="Schema version" value={record.schema_version} />
          <SectionRow label="Onboarded by" value={record.created_by} />
          {record.cert_subject_dn !== null && (
            <SectionRow label="Cert subject" value={record.cert_subject_dn} />
          )}
        </>
      )}
    </SectionShell>
  );
}

function RecentErrorsSection(): React.ReactElement {
  return (
    <SectionShell id="recent_errors" title="Recent errors">
      <SectionPlaceholder note="Decoder / auth error stream pending." />
    </SectionShell>
  );
}
